import { ref } from "vue";
import { useAuth } from "./useAuth";
import { readUserFacingError, type UserFacingError } from "../errorMessages";
import type { JobInputKind } from "../utils/inputKind";

/**
 * Seconds range on the ORIGINAL video's clock (RallyClip v1,
 * docs/design/rallyclip-v1/data-schemas.md "Common types and manifest").
 */
export interface TimeRange {
  start_seconds: number;
  end_seconds: number;
}

/**
 * The checked v1 source block on a completed detail response
 * (api-schemas.md "Get the job result"). ``byte_length`` is a decimal
 * u64 STRING on the wire and stays a string here — a multi-GB source
 * exceeds Number's safe-integer range, so it must never pass through
 * ``Number()``/``parseInt`` (format with ``formatDecimalBytes``).
 */
export interface V1Source {
  sha256: string;
  byte_length: string;
  playable_range: TimeRange;
}

/**
 * One rally marker. v0 markers carry ``hl_start_s`` (a seek offset in
 * the concatenated highlights reel); v1 markers carry ``hl_start_s:
 * null`` plus ``source_range`` on the original video's clock. The
 * integer ``rally`` identity is the server's and is never re-numbered
 * client-side. Other worker fields ride along untyped.
 */
export interface RallyMarker {
  rally: number;
  hl_start_s: number | null;
  source_range?: TimeRange | null;
  source_start_s?: number;
  duration_s?: number;
  game?: number;
}

export interface JobSummary {
  job_id: string;
  job_name: string;
  status: JobStatus;
  num_segments: number | null;
  created_at: string;
  // Structured failure classification on list rows (same vocabulary
  // as JobDetail.error_code). Optional: absent against an older
  // backend.
  error_code?: string | null;
  // RallyClip v1: ``device_detections_v1`` for phone detection-bundle
  // jobs. ABSENT for v0 by contract (the API omits the key), so read
  // it only through ``utils/inputKind.ts#inputKindOf``.
  input_kind?: JobInputKind;
  // Presigned GET URL for the job-card thumbnail, non-null from
  // thumbnail-ready onward (customer-first delivery — see
  // segment-web-customer-done-adoption-one-pager.md). HomeView uses
  // PRESENCE as the render gate but keeps the proxy route as the
  // <img> src (C-W7: the proxy stays the only credential boundary).
  // Optional: absent against a pre-#1092 backend (C-W6).
  thumbnail_url?: string | null;
  // Customer-facing done (see displayStatus/displayDone in
  // utils/jobDisplay.ts — C-W1: the only done signal). Optional:
  // absent against an older backend, in which case the badge and tab
  // title simply flip at terminal status as today (C-W6).
  customer_done?: boolean | null;
  // Valid empty/no-rallies outcome. When true, completion is real but
  // no highlights preview is expected.
  empty_result?: boolean | null;
  // Server-owned retry decision is also present on list rows so clients
  // with Home-level recovery actions do not need to duplicate policy.
  can_retry?: boolean;
  retry_limit?: number | null;
  retries_remaining?: number | null;
  next_retry_at?: string | null;
  // ``invalid_input`` is the RallyClip v1 verified-malformed-bundle
  // block (api-schemas.md "Retry a failed job"): permanent, so the SPA
  // hides Retry instead of disabling it.
  retry_block_reason?:
    | "wrong_status"
    | "retry_limit_reached"
    | "retry_cooldown"
    | "invalid_input"
    | null;
}

/**
 * Per-artifact readiness vocabulary from progress snapshot v2
 * (customer-first-artifact-delivery-one-pager.md). The SPA reads ONLY
 * `artifacts.hd === "ready"` (the HD download affordance, C-W5);
 * done-ness comes exclusively from `customer_done` (C-W1), never from
 * `artifacts.preview`.
 */
type ArtifactState = "pending" | "ready" | "skipped" | "failed";

export interface JobDetail extends JobSummary {
  // Either video_filename (file-upload jobs) or source_url
  // (URL-ingest YouTube jobs from Phase 2) is set; never both.
  // SPA branches the failure UX on source_url presence. Both are
  // optional-with-null so legacy test fixtures that pre-date Phase 2
  // can omit them without breaking the type.
  video_filename: string | null;
  source_url?: string | null;
  preview_frames: number;
  timeline_path: string | null;
  runpod_job_id: string | null;
  // Structured error code (gRPC status name, e.g. "UNAVAILABLE").
  // The SPA dispatches on this via USER_MESSAGES in errorMessages.ts.
  // NEVER look at JobResponse.error — operator-grade content. See
  // structured-error-reporting-one-pager.md (C-S4). The omission of
  // `error` from this type is the compile-time enforcement of that
  // constraint.
  error_code: string | null;
  updated_at: string;
  // When the current attempt started. Equals created_at on the
  // initial submission, bumped by the retry endpoint. ProgressPanel
  // and the "Ready in:" sub-line MUST anchor on this — never on
  // created_at — otherwise a retried job's elapsed/total reflects the
  // gap from original submission, not the current attempt. Nullable
  // on the wire for legacy rows during the rollout window; consumers
  // fall back to created_at when absent. See
  // docs/design/retry-failed-jobs-one-pager.md elapsed-anchor.
  attempt_started_at?: string | null;
  // Per-job usage metrics for the result-framed completed panel
  // (design: usage-display-one-pager.md). All three may be null —
  // legacy jobs from before Phase 1, or jobs where a side fetch
  // failed during completion. The view omits the corresponding
  // sub-line field when null.
  input_bytes?: number | null;
  video_duration_ms?: number | null;
  end_to_end_ms?: number | null;
  // Worker progress (design: worker-progress-eta-one-pager.md).
  // `runpod_state` carries the queued / active / done sub-state
  // inside status='processing' (C33). `progress` is the whitelist-
  // filtered public view of the worker snapshot (C35) — only
  // `version`, `overall_percentage`, and `updated_at` are visible
  // here; internal pipeline vocabulary (stage names, labels,
  // diagnostics) stays server-side.
  runpod_state?: "queued" | "active" | "done" | null;
  progress?: {
    version: number;
    overall_percentage: number;
    updated_at: string;
    // Snapshot v2 blocks (customer-first delivery). Both optional —
    // absent for v1 snapshots and pre-#1092 backends (C-W6).
    artifacts?: {
      thumbnail?: ArtifactState;
      preview?: ArtifactState;
      hd?: ArtifactState;
    };
    // Allowlisted worker facts. Consumed by the Node proxy's B2 key
    // derivation (`meta.clip_stem`), NOT by the SPA — no `meta` value
    // may render in the UI (C-W4; enforced by structuralLeakGuard).
    meta?: {
      num_segments?: number;
      fps?: number;
      num_frames?: number;
      clip_stem?: string;
      transcoded_available?: boolean;
      empty_result?: boolean;
    };
  } | null;
  // `customer_done` is inherited from JobSummary: the 360p preview is
  // delivered (uploaded + QA-passed) even while `status` is still
  // `processing` during the ~25–40s operator-artifact finalize tail.
  // True iff the worker wrote _highlights_360p.mp4 to B2 — the file
  // the SPA's inline preview <video> plays. The flag tracks the 360p
  // variant (not the HD file) because the user-facing /highlights
  // endpoint serves 360p; HD remains in B2 reserved for a future
  // credit-gated download. See
  // docs/design/highlights-preview-360p-one-pager.md. Null for legacy
  // jobs from before the worker learned to report this field.
  highlights_available?: boolean | null;
  // Number of user-initiated retries plus the server-owned eligibility
  // decision. These fields deliberately replace the old client-side
  // MAX_RETRIES mirror (issue #1722).
  retry_count?: number;
  // Per-rally markers (detail-only). Present for completed jobs with
  // rallies; null before completion and for a checked zero-rally
  // result. See RallyMarker for the v0/v1 timing split.
  rally_markers?: RallyMarker[] | null;
  // RallyClip v1 checked source (api-schemas.md "Get the job result").
  // Null until a checked result; ABSENT for v0 (key omitted).
  source?: V1Source | null;
  // Presigned artifact URLs minted for non-browser clients. The SPA
  // never plays from these — its player stays on the proxy routes
  // (C-W7) — they are typed so a v1 job's nulls are visible to tests.
  // For v1 all three highlight URLs are null by contract.
  timeline_url?: string | null;
  highlights_url?: string | null;
  highlights_hd_url?: string | null;
}

export type JobStatus = "pending_upload" | "processing" | "completed" | "failed";

const jobs = ref<JobSummary[]>([]);
// `listError` carries both the user-facing message AND the operator-
// pasteable Jaeger trace_id (when the backend supplied one). HomeView
// renders `message`; the optional `Reference:` line below it uses
// `traceId`. See ``docs/design/error-correlation-one-pager.md``.
const listError = ref<UserFacingError | null>(null);

/**
 * Module-level state so HomeView's list and JobDetailView's per-job
 * lookups share one cached snapshot. (Sidebar no longer consumes the
 * list as of the UX polish — Decision #5 moved the list into HomeView.)
 * `listError` surfaces failures from `loadJobs()`; per-job fetches
 * surface their own errors via the returned `{ job, error }` shape.
 */
export function useJobs() {
  return {
    jobs,
    listError,
    loadJobs,
    fetchJob,
    /** Test-only. */
    _resetForTests,
  };
}

async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  const resp = await fetch(input, { credentials: "include", ...init });
  if (resp.status === 401) {
    // Server-side session is gone; clear local auth so the router guard
    // can bounce the user to /login on the next navigation.
    const { signOut } = useAuth();
    await signOut();
  }
  return resp;
}

async function loadJobs(): Promise<void> {
  listError.value = null;
  try {
    const resp = await apiFetch("/api/jobs");
    if (resp.status === 401) {
      // apiFetch already called signOut() → App.vue's SIGNED_OUT_EVENT
      // listener will route to /login. Don't flash an error in the
      // sidebar before that route fires; mirrors the fetchJob()
      // unauthenticated handling.
      jobs.value = [];
      return;
    }
    if (!resp.ok) {
      // C-S8 (sync-channel separation): render USER_MESSAGES[code]
      // for Problem Details bodies, generic-by-status otherwise.
      // NEVER the raw response body — that's how the original 403
      // leak ("{"type":"/errors/PERMISSION_DENIED",...}") got
      // rendered. See docs/design/sync-channel-leak-fix-one-pager.md.
      listError.value = await readUserFacingError(resp);
      jobs.value = [];
      return;
    }
    jobs.value = (await resp.json()) as JobSummary[];
  } catch (err) {
    // Network failure (CORS, DNS, fetch threw) — no Response to inspect,
    // so no traceId surface. Render message only.
    listError.value = { message: `Cannot reach backend: ${(err as Error).message}` };
    jobs.value = [];
  }
}

export interface FetchJobResult {
  job: JobDetail | null;
  /** User-facing message string when the fetch failed; null on success.
   *  Bundled with ``traceId`` when the backend supplied one — render
   *  alongside the message via the same TraceReference component the
   *  sidebar uses. */
  error: string | null;
  /** Operator-pasteable Jaeger key for this failed fetch; undefined when
   *  tracing is off or the upstream didn't propagate it. See
   *  ``docs/design/error-correlation-one-pager.md``. */
  errorTraceId?: string;
  /** True iff the upstream returned 401 / the local session was cleared. Lets
   *  consumers (e.g. JobDetailView polling) stop polling without surfacing a
   *  generic-status banner before the route change kicks in. */
  unauthenticated?: boolean;
}

async function fetchJob(jobId: string): Promise<FetchJobResult> {
  try {
    const resp = await apiFetch(`/api/jobs/${jobId}`);
    if (resp.status === 401) {
      return { job: null, error: "Session expired.", unauthenticated: true };
    }
    if (resp.status === 404) {
      return { job: null, error: "Job not found." };
    }
    if (!resp.ok) {
      // C-S8 sync-channel — see loadJobs for rationale.
      const ufe = await readUserFacingError(resp);
      return { job: null, error: ufe.message, errorTraceId: ufe.traceId };
    }
    return { job: (await resp.json()) as JobDetail, error: null };
  } catch (err) {
    return { job: null, error: `Cannot reach backend: ${(err as Error).message}` };
  }
}

function _resetForTests(): void {
  jobs.value = [];
  listError.value = null;
}
